import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Pagination,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  Add,
  Delete,
  Edit,
  Search,
} from "@mui/icons-material";

import api from "../../../services/api";

const CoachBase = () => {
  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  // =========================================================
  // STATE
  // =========================================================

  const [coachBases, setCoachBases] = useState([]);
  const [divisions, setDivisions] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [dialogOpen, setDialogOpen] =
    useState(false);

  const [editId, setEditId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [form, setForm] = useState({
    base_code: "",
    base_name: "",
    division_id: "",
    status: "Active",
  });

  // =========================================================
  // MESSAGE
  // =========================================================

  const showMessage = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // =========================================================
  // LOAD DIVISIONS
  // =========================================================

  const loadDivisions = async () => {
    try {
      const response =
        await api.get("/divisions");

      if (response.data.success) {
        setDivisions(
          response.data.data || []
        );
      } else {
        showMessage(
          response.data.message ||
            "Unable to load divisions",
          "error"
        );
      }
    } catch (error) {
      console.error(
        "Load divisions:",
        error
      );

      showMessage(
        error.response?.data?.message ||
          "Unable to load divisions",
        "error"
      );
    }
  };

  // =========================================================
  // LOAD COACH BASES
  // =========================================================

  const loadCoachBases = async () => {
    try {
      const response =
        await api.get("/coach-bases");

      if (response.data.success) {
        setCoachBases(
          response.data.data || []
        );
      } else {
        showMessage(
          response.data.message ||
            "Unable to load Coach Bases",
          "error"
        );
      }
    } catch (error) {
      console.error(
        "Load Coach Bases:",
        error
      );

      showMessage(
        error.response?.data?.message ||
          "Unable to load Coach Bases",
        "error"
      );
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        await Promise.all([
          loadDivisions(),
          loadCoachBases(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // =========================================================
  // SEARCH / FILTER
  // =========================================================

  const filteredData = useMemo(() => {
    const value = search
      .trim()
      .toLowerCase();

    if (!value) {
      return coachBases;
    }

    return coachBases.filter((item) => {
      return [
        item.base_code,
        item.base_name,
        item.division_code,
        item.division_name,
        item.railway_code,
        item.railway_name,
        item.status,
      ].some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(value)
      );
    });
  }, [coachBases, search]);

  // =========================================================
  // TOTAL PAGES
  // =========================================================

  const totalPages = Math.ceil(
    filteredData.length / rowsPerPage
  );

  // =========================================================
  // PAGINATED DATA
  // =========================================================

  const paginatedData = useMemo(() => {
    const startIndex =
      (page - 1) * rowsPerPage;

    return filteredData.slice(
      startIndex,
      startIndex + rowsPerPage
    );
  }, [
    filteredData,
    page,
    rowsPerPage,
  ]);

  // =========================================================
  // KEEP PAGE VALID
  // =========================================================

  useEffect(() => {
    if (totalPages === 0) {
      if (page !== 1) {
        setPage(1);
      }

      return;
    }

    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  // =========================================================
  // PAGINATION INFO
  // =========================================================

  const startRecord =
    filteredData.length === 0
      ? 0
      : (page - 1) * rowsPerPage + 1;

  const endRecord = Math.min(
    page * rowsPerPage,
    filteredData.length
  );

  // =========================================================
  // PAGE CHANGE
  // =========================================================

  const handlePageChange = (
    event,
    value
  ) => {
    setPage(value);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // RESET FORM
  // =========================================================

  const resetForm = () => {
    setForm({
      base_code: "",
      base_name: "",
      division_id: "",
      status: "Active",
    });

    setEditId(null);
  };

  // =========================================================
  // ADD
  // =========================================================

  const handleOpenAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (item) => {
    setEditId(item.id);

    setForm({
      base_code: item.base_code || "",
      base_name: item.base_name || "",
      division_id:
        item.division_id ?? "",
      status:
        item.status || "Active",
    });

    setDialogOpen(true);
  };

  // =========================================================
  // CLOSE DIALOG
  // =========================================================

  const handleCloseDialog = () => {
    if (loading) return;

    setDialogOpen(false);
    resetForm();
  };

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // SAVE
  // =========================================================

  const handleSave = async () => {
    if (!form.base_code.trim()) {
      showMessage(
        "Base Code is required",
        "error"
      );
      return;
    }

    if (!form.base_name.trim()) {
      showMessage(
        "Base Name is required",
        "error"
      );
      return;
    }

    if (!form.division_id) {
      showMessage(
        "Please select Division",
        "error"
      );
      return;
    }

    try {
      setLoading(true);

      const payload = {
        base_code:
          form.base_code.trim(),

        base_name:
          form.base_name.trim(),

        division_id:
          Number(form.division_id),

        status: form.status,
      };

      // =====================================================
      // UPDATE
      // =====================================================

      if (editId !== null) {
        const response =
          await api.put(
            `/coach-bases/${editId}`,
            payload
          );

        if (!response.data.success) {
          throw new Error(
            response.data.message ||
              "Coach Base update failed"
          );
        }

        showMessage(
          "Coach Base updated successfully"
        );
      }

      // =====================================================
      // CREATE
      // =====================================================

      else {
        const response =
          await api.post(
            "/coach-bases",
            payload
          );

        if (!response.data.success) {
          throw new Error(
            response.data.message ||
              "Coach Base creation failed"
          );
        }

        showMessage(
          "Coach Base added successfully"
        );
      }

      await loadCoachBases();

      // Return to first page after save
      setPage(1);

      setDialogOpen(false);
      resetForm();

    } catch (error) {
      console.error(
        "Save Coach Base:",
        error
      );

      showMessage(
        error.response?.data?.message ||
          error.message ||
          "Unable to save Coach Base",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async (id) => {
    const confirmed =
      window.confirm(
        "Are you sure you want to delete this Coach Base?"
      );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      const response =
        await api.delete(
          `/coach-bases/${id}`
        );

      if (!response.data.success) {
        throw new Error(
          response.data.message ||
            "Coach Base deletion failed"
        );
      }

      await loadCoachBases();

      showMessage(
        "Coach Base deleted successfully"
      );

    } catch (error) {
      console.error(
        "Delete Coach Base:",
        error
      );

      showMessage(
        error.response?.data?.message ||
          error.message ||
          "Unable to delete Coach Base",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // DIVISION DISPLAY
  // =========================================================

  const getDivisionName = (
    divisionId
  ) => {
    const division =
      divisions.find(
        (item) =>
          Number(item.id) ===
          Number(divisionId)
      );

    if (!division) {
      return "-";
    }

    return `${division.code} - ${division.name}`;
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box sx={{ width: "100%" }}>

      {/* =====================================================
          MAIN CARD
      ===================================================== */}

      <Card
        elevation={0}
        sx={{
          width: "100%",
          border:
            "1px solid #e2e8f0",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >

        {/* =================================================
            TOOLBAR
        ================================================= */}

        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 2,
            justifyContent:
              "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >

          {/* SEARCH */}

          <TextField
            size="small"
            placeholder="Search Coach Base..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 380,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* ACTIONS */}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{
              xs: "space-between",
              sm: "flex-end",
            }}
          >

            <Chip
              label={`${filteredData.length} Records`}
              variant="outlined"
            />

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAdd}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
              }}
            >
              Add Coach Base
            </Button>

          </Stack>
        </Box>

        {/* =================================================
            DESKTOP
        ================================================= */}

        {!isMobile ? (
          <>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                width: "100%",
                overflowX: "auto",
                borderTop:
                  "1px solid #e2e8f0",
              }}
            >

              <Table
                sx={{
                  minWidth: 800,
                }}
              >

                {/* TABLE HEADER */}

                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        "#f8fafc",
                    }}
                  >

                    <TableCell
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      #
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      Base Code
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      Base Name
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      Division
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      Railway
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      Status
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      Actions
                    </TableCell>

                  </TableRow>
                </TableHead>

                {/* TABLE BODY */}

                <TableBody>

                  {paginatedData.map(
                    (item, index) => (
                      <TableRow
                        key={item.id}
                        hover
                      >

                        {/* SERIAL */}

                        <TableCell>
                          {(page - 1) *
                            rowsPerPage +
                            index +
                            1}
                        </TableCell>

                        {/* BASE CODE */}

                        <TableCell>
                          <Typography
                            fontWeight={700}
                          >
                            {item.base_code}
                          </Typography>
                        </TableCell>

                        {/* BASE NAME */}

                        <TableCell>
                          <Typography
                            fontWeight={600}
                          >
                            {item.base_name}
                          </Typography>
                        </TableCell>

                        {/* DIVISION */}

                        <TableCell>
                          {item.division_code ||
                          item.division_name
                            ? `${item.division_code || ""}${
                                item.division_code &&
                                item.division_name
                                  ? " - "
                                  : ""
                              }${
                                item.division_name ||
                                ""
                              }`
                            : getDivisionName(
                                item.division_id
                              )}
                        </TableCell>

                        {/* RAILWAY */}

                        <TableCell>
                          {item.railway_code ||
                          item.railway_name
                            ? `${item.railway_code || ""}${
                                item.railway_code &&
                                item.railway_name
                                  ? " - "
                                  : ""
                              }${
                                item.railway_name ||
                                ""
                              }`
                            : "-"}
                        </TableCell>

                        {/* STATUS */}

                        <TableCell>
                          <Chip
                            label={
                              item.status
                            }
                            size="small"
                            color={
                              item.status ===
                              "Active"
                                ? "success"
                                : "default"
                            }
                          />
                        </TableCell>

                        {/* ACTIONS */}

                        <TableCell align="right">

                          <IconButton
                            color="primary"
                            onClick={() =>
                              handleEdit(
                                item
                              )
                            }
                          >
                            <Edit />
                          </IconButton>

                          <IconButton
                            color="error"
                            onClick={() =>
                              handleDelete(
                                item.id
                              )
                            }
                          >
                            <Delete />
                          </IconButton>

                        </TableCell>

                      </TableRow>
                    )
                  )}

                  {/* NO DATA */}

                  {filteredData.length ===
                    0 && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{
                          py: 5,
                        }}
                      >
                        {loading
                          ? "Loading..."
                          : "No Coach Base found"}
                      </TableCell>
                    </TableRow>
                  )}

                </TableBody>

              </Table>

            </TableContainer>

            {/* =================================================
                DESKTOP PAGINATION
            ================================================= */}

            {filteredData.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  gap: 2,
                  px: 2,
                  py: 2,
                  flexWrap: "wrap",
                  borderTop:
                    "1px solid #e2e8f0",
                }}
              >

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Showing{" "}
                  <strong>
                    {startRecord}
                  </strong>{" "}
                  -{" "}
                  <strong>
                    {endRecord}
                  </strong>{" "}
                  of{" "}
                  <strong>
                    {filteredData.length}
                  </strong>{" "}
                  records
                </Typography>

                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={
                      handlePageChange
                    }
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                  />
                )}

              </Box>
            )}

          </>
        ) : (

          /* ===================================================
             MOBILE
          =================================================== */

          <>

            <Box sx={{ p: 1.5 }}>

              {paginatedData.map(
                (item, index) => (

                  <Card
                    key={item.id}
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      border:
                        "1px solid #e2e8f0",
                      borderRadius: 2,
                    }}
                  >

                    <CardContent>

                      {/* TOP */}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "flex-start",
                          gap: 1,
                        }}
                      >

                        <Box>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            #
                            {(page - 1) *
                              rowsPerPage +
                              index +
                              1}
                          </Typography>

                          <Typography
                            fontWeight={800}
                            sx={{
                              mt: 0.3,
                            }}
                          >
                            {item.base_name}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            Code:{" "}
                            {item.base_code}
                          </Typography>

                        </Box>

                        <Chip
                          label={
                            item.status
                          }
                          size="small"
                          color={
                            item.status ===
                            "Active"
                              ? "success"
                              : "default"
                          }
                        />

                      </Box>

                      <Divider
                        sx={{
                          my: 1.5,
                        }}
                      />

                      {/* DIVISION */}

                      <Typography
                        variant="body2"
                      >
                        <strong>
                          Division:
                        </strong>{" "}
                        {item.division_code ||
                        item.division_name
                          ? `${item.division_code || ""}${
                              item.division_code &&
                              item.division_name
                                ? " - "
                                : ""
                            }${
                              item.division_name ||
                              ""
                            }`
                          : getDivisionName(
                              item.division_id
                            )}
                      </Typography>

                      {/* RAILWAY */}

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                        }}
                      >
                        <strong>
                          Railway:
                        </strong>{" "}
                        {item.railway_code ||
                        item.railway_name
                          ? `${item.railway_code || ""}${
                              item.railway_code &&
                              item.railway_name
                                ? " - "
                                : ""
                            }${
                              item.railway_name ||
                              ""
                            }`
                          : "-"}
                      </Typography>

                      {/* ACTIONS */}

                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          mt: 1.5,
                        }}
                      >

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            <Edit />
                          }
                          onClick={() =>
                            handleEdit(
                              item
                            )
                          }
                        >
                          Edit
                        </Button>

                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={
                            <Delete />
                          }
                          onClick={() =>
                            handleDelete(
                              item.id
                            )
                          }
                        >
                          Delete
                        </Button>

                      </Stack>

                    </CardContent>

                  </Card>

                )
              )}

              {/* NO DATA */}

              {filteredData.length ===
                0 && (
                <Box
                  sx={{
                    textAlign:
                      "center",
                    py: 5,
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    {loading
                      ? "Loading..."
                      : "No Coach Base found"}
                  </Typography>
                </Box>
              )}

            </Box>

            {/* =================================================
                MOBILE PAGINATION
            ================================================= */}

            {filteredData.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1,
                  py: 2,
                  borderTop:
                    "1px solid #e2e8f0",
                }}
              >

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Showing{" "}
                  <strong>
                    {startRecord}
                  </strong>{" "}
                  -{" "}
                  <strong>
                    {endRecord}
                  </strong>{" "}
                  of{" "}
                  <strong>
                    {filteredData.length}
                  </strong>
                </Typography>

                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={
                      handlePageChange
                    }
                    color="primary"
                    shape="rounded"
                    size="small"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                  />
                )}

              </Box>
            )}

          </>
        )}

      </Card>

      {/* =====================================================
          ADD / EDIT DIALOG
      ===================================================== */}

      <Dialog
        open={dialogOpen}
        onClose={
          handleCloseDialog
        }
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >

        <DialogTitle
          sx={{
            fontWeight: 800,
          }}
        >
          {editId !== null
            ? "Edit Coach Base"
            : "Add Coach Base"}
        </DialogTitle>

        <DialogContent dividers>

          <Stack
            spacing={2.2}
            sx={{
              pt: 1,
            }}
          >

            {/* BASE CODE */}

            <TextField
              fullWidth
              label="Base Code"
              name="base_code"
              value={
                form.base_code
              }
              onChange={
                handleChange
              }
              required
            />

            {/* BASE NAME */}

            <TextField
              fullWidth
              label="Base Name"
              name="base_name"
              value={
                form.base_name
              }
              onChange={
                handleChange
              }
              required
            />

            {/* DIVISION */}

            <TextField
              select
              fullWidth
              label="Division"
              name="division_id"
              value={
                form.division_id
              }
              onChange={
                handleChange
              }
              required
            >

              <MenuItem value="">
                Select Division
              </MenuItem>

              {divisions
                .filter(
                  (division) =>
                    division.status ===
                    "Active"
                )
                .map(
                  (division) => (
                    <MenuItem
                      key={
                        division.id
                      }
                      value={
                        division.id
                      }
                    >
                      {division.code} -{" "}
                      {division.name}

                      {division.railway_code
                        ? ` (${division.railway_code})`
                        : ""}
                    </MenuItem>
                  )
                )}

            </TextField>

            {/* STATUS */}

            <TextField
              select
              fullWidth
              label="Status"
              name="status"
              value={
                form.status
              }
              onChange={
                handleChange
              }
            >

              <MenuItem value="Active">
                Active
              </MenuItem>

              <MenuItem value="Inactive">
                Inactive
              </MenuItem>

            </TextField>

          </Stack>

        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
          }}
        >

          <Button
            onClick={
              handleCloseDialog
            }
            disabled={loading}
            sx={{
              textTransform:
                "none",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={
              handleSave
            }
            disabled={loading}
            sx={{
              textTransform:
                "none",
              px: 3,
            }}
          >
            {loading
              ? "Saving..."
              : editId !== null
              ? "Update"
              : "Save"}
          </Button>

        </DialogActions>

      </Dialog>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={3000}
        onClose={
          handleSnackbarClose
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >

        <Alert
          severity={
            snackbar.severity
          }
          onClose={
            handleSnackbarClose
          }
          variant="filled"
        >
          {snackbar.message}
        </Alert>

      </Snackbar>

    </Box>
  );
};

export default CoachBase;